import { useState } from 'react';
import { Mail, MessageSquare, User as UserIcon, Send, CheckCircle2, MapPin, Clock, Phone, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import SiteFooter from '../components/SiteFooter';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { contactFaqs } from '../mock/legalContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactUs = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('general');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const { t, tr } = useLang();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError(t('fillRequired'));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        subject,
        message: message.trim(),
      });
      setSent(true);
      setName('');
      setEmail('');
      setSubject('general');
      setMessage('');
    } catch (err) {
      setError(err?.response?.data?.detail || t('couldNotSend'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0e10] text-white">
      <Header />
      <main className="px-4 md:px-8 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center fade-in-up">
            <p className="text-[#ff4e15] font-bold text-sm tracking-widest">{t('contactPre')}</p>
            <h1 className="heading-font text-4xl md:text-5xl font-black mt-2 tracking-wide">
              {t('contactTitle')}
            </h1>
            <p className="mt-3 text-white/70 max-w-xl mx-auto">
              {t('contactSub')}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-[#1a1b1e] border border-[#2a2b2f] rounded-md p-6">
              {sent ? (
                <div className="text-center py-10 fade-in-up" data-testid="contact-sent">
                  <div className="w-16 h-16 rounded-full bg-[#ff4e15]/20 border-2 border-[#ff4e15] flex items-center justify-center mx-auto">
                    <CheckCircle2 size={32} className="text-[#ff4e15]" />
                  </div>
                  <h2 className="heading-font text-2xl font-black mt-4 tracking-wide">{t('messageSent')}</h2>
                  <p className="mt-2 text-white/70 text-sm">
                    {t('messageSentSub')}
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-6 px-5 py-2 border border-[#2a2b2f] hover:border-[#ff4e15] rounded-sm text-sm text-white/80 hover:text-[#ff4e15] transition-colors"
                    data-testid="contact-send-another-btn"
                  >
                    {t('sendAnother')}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <h2 className="text-lg font-bold mb-2">{t('sendUsMessage')}</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="relative">
                      <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('yourNamePlaceholder')}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-[#141518] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('emailAddressPlaceholder')}
                        required
                        className="w-full pl-10 pr-4 py-3 bg-[#141518] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-white/50 uppercase tracking-wide mb-1.5 block">{t('subject')}</label>
                    <select
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-4 py-3 bg-[#141518] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors"
                      data-testid="contact-subject-select"
                    >
                      <option value="general">{t('subjectGeneral')}</option>
                      <option value="support">{t('subjectSupport')}</option>
                      <option value="billing">{t('subjectBilling')}</option>
                      <option value="feedback">{t('subjectFeedback')}</option>
                      <option value="partnership">{t('subjectPartnership')}</option>
                      <option value="other">{t('subjectOther')}</option>
                    </select>
                  </div>

                  <div className="relative">
                    <MessageSquare size={16} className="absolute left-3 top-3 text-white/40" />
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('howCanWeHelp')}
                      rows={6}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-[#141518] border border-[#2a2b2f] focus:border-[#ff4e15] outline-none rounded-sm text-white transition-colors resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {error && (
                    <div className="flex items-start gap-2 text-sm text-[#ff4e15] bg-[#1f1210] border border-[#ff4e15]/40 px-3 py-2 rounded-sm">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full py-3.5 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    <Send size={16} />
                    {busy ? t('sending') : t('sendMessage')}
                  </button>

                  <p className="text-[10px] text-white/40 text-center">
                    {t('submitAgree')}{' '}
                    <Link to="/privacy-policy" className="text-[#ff4e15] hover:underline">{t('privacyPolicy')}</Link>.
                  </p>
                </form>
              )}
            </div>

            {/* Contact info sidebar */}
            <div className="space-y-4">
              <InfoCard icon={Mail} title={t('emailUs')}>
                <a href="mailto:support@myhealthyfy.com" className="block text-sm text-white hover:text-[#ff4e15]">
                  support@myhealthyfy.com
                </a>
                <a href="mailto:hello@myhealthyfy.com" className="block text-sm text-white/80 hover:text-[#ff4e15]">
                  hello@myhealthyfy.com
                </a>
              </InfoCard>

              <InfoCard icon={Phone} title={t('phone')}>
                <p className="text-sm text-white">+1 (555) 010-2210</p>
                <p className="text-xs text-white/50 mt-0.5">{t('phoneHours')}</p>
              </InfoCard>

              <InfoCard icon={MapPin} title={t('address')}>
                <p className="text-sm text-white leading-relaxed">
                  Myhealthyfy HQ<br />
                  221B Wellness Avenue<br />
                  Bengaluru, India
                </p>
              </InfoCard>

              <InfoCard icon={Clock} title={t('responseTime')}>
                <p className="text-sm text-white">{t('within12')}</p>
                <p className="text-xs text-white/50 mt-0.5">{t('priorityFor')}</p>
              </InfoCard>
            </div>
          </div>

          {/* FAQs */}
          <div className="mt-12">
            <h2 className="text-xl font-bold mb-4">{t('faqTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {contactFaqs.map((f, i) => (
                <div key={i} className="p-4 bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm">
                  <div className="font-semibold text-white text-sm">{tr(f.q)}</div>
                  <div className="text-xs text-white/70 mt-2 leading-relaxed">{tr(f.a)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="p-4 bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-sm bg-[#ff4e15]/15 flex items-center justify-center">
        <Icon size={16} className="text-[#ff4e15]" />
      </div>
      <div className="text-xs uppercase tracking-wide text-white/60 font-semibold">{title}</div>
    </div>
    <div className="pl-10">{children}</div>
  </div>
);

export default ContactUs;
